<template>
  <div class="top barging-container">
    <div class="barging-card" style="padding: 0; margin-top: 0; background-color: #f2f7fa; border-radius: 10px;">

      <div class="w-full searchBar" ref="searchBarRef" id="delayReportBar">
        <div class="flex flex-wrap justify-end">
          <div class="flex flex-row py-2 pr-1">
            <el-date-picker
              v-model="params.dateRange"
              type="datetimerange"
              size="mini"
              start-placeholder="ETA Start"
              end-placeholder="ETA End"
              format="yyyy-MM-dd HH:mm:ss"
              value-format="yyyy-MM-dd HH:mm:ss"
              :default-time="['00:00:00', '23:59:59']"
              :style="{ width: '380px' }"
            />
          </div>

          <div class="flex flex-row py-2 pr-1">
            <el-input
              v-model="params.nominationId"
              size="mini"
              clearable
              placeholder="Nomination ID"
              :style="{ width: '150px' }"
            />
          </div>

          <div class="flex flex-row pl-1 py-2">
            <el-button size="mini" type="primary" @click="handleSearch">Search</el-button>
            <el-button v-if="userInfo && userInfo.role == 1" size="mini" type="success" :loading="exporting" @click="handleExport">Export Excel</el-button>
          </div>
        </div>
      </div>

      <div class="box-card barging-table" v-bind:class="{ 'mobile-view': isMobileScreen }">
        <div class="w-full Comtable" id="delayReportTable" ref="tableRef">
          <el-table
            :data="listData"
            :header-cell-style="tableHeaderStyle"
            :cell-style="getCellStyle"
            table-layout="auto"
            height="100%"
            v-loading="loading"
          >
            <el-table-column align="left" prop="nominationId" label="Nomination ID" min-width="140" />

            <el-table-column align="left" prop="date" label="Date" min-width="160">
              <template slot-scope="scope">
                {{ formatDate(scope.row.date) }}
              </template>
            </el-table-column>

            <el-table-column align="left" prop="causeOfDelay" label="Cause of Delay" min-width="220" />

            <el-table-column align="left" prop="fromTime" label="Delayed From" min-width="180">
              <template slot-scope="scope">
                {{ formatDateTime(scope.row.fromTime) }}
              </template>
            </el-table-column>

            <el-table-column align="left" prop="toTime" label="Delayed To" min-width="180">
              <template slot-scope="scope">
                {{ formatDateTime(scope.row.toTime) }}
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>

      <div class="barging-pagination">
        <pagination
          v-show="total > 0"
          :total="total"
          :page.sync="params.pageNum"
          :limit.sync="params.pageSize"
          :pageSizes="[20, 30, 50, 100]"
          @pagination="getList"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { getDelayReport, exportDelayReport } from "@/services/form.js";
import { downloadFile } from "@/services/index.js";
import { mapState } from "vuex";

export default {
  name: "DelayReport",
  data() {
    return {
      listData: [],
      total: 0,
      loading: false,
      exporting: false,
      params: {
        pageNum: 1,
        pageSize: 20,
        dateRange: null,
        nominationId: null,
      },
    };
  },
  computed: {
    ...mapState(["screenType", "userInfo"]),
    isMobileScreen() {
      return this.screenType?.isMobileScreen ?? false;
    },
    tableHeaderStyle() {
      return {
        height: "55px",
        background: "#fff",
        "border-bottom": "2px solid #D6D9E1",
      };
    },
  },
  created() {
    this.getList();
  },
  mounted() {
    this.adjustHeight();
    window.addEventListener("resize", this.adjustHeight);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.adjustHeight);
  },
  methods: {
    adjustHeight() {
      const barEl = document.getElementById("delayReportBar");
      const tableEl = document.getElementById("delayReportTable");
      if (!barEl || !tableEl) return;
      const barHeight = barEl.offsetHeight;
      const isMobile = window.innerWidth < 1025;
      const overhead = isMobile ? 235 : 202;
      tableEl.style.height = `calc(100vh - ${barHeight + overhead}px)`;
    },

    async getList() {
      this.loading = true;
      try {
        const payload = {
          pageNum: this.params.pageNum,
          pageSize: this.params.pageSize,
        };
        if (this.params.dateRange && this.params.dateRange.length === 2) {
          payload.startDate = this.params.dateRange[0];
          payload.endDate = this.params.dateRange[1];
        }
        if (this.params.nominationId) {
          payload.nominationId = this.params.nominationId;
        }
        const res = await getDelayReport(payload);
        this.listData = res.rows || [];
        this.total = res.total || 0;
      } finally {
        this.loading = false;
      }
    },

    handleSearch() {
      this.params.pageNum = 1;
      this.getList();
    },

    handleReset() {
      this.params.dateRange = null;
      this.params.nominationId = null;
      this.params.pageNum = 1;
      this.getList();
    },

    async handleExport() {
      this.exporting = true;
      try {
        const payload = {};
        if (this.params.dateRange && this.params.dateRange.length === 2) {
          payload.startDate = this.params.dateRange[0];
          payload.endDate = this.params.dateRange[1];
        }
        if (this.params.nominationId) {
          payload.nominationId = this.params.nominationId;
        }
        const data = await exportDelayReport(payload);
        const today = new Date();
        const ymd = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
        downloadFile(data, `stoppages-${ymd}.xls`);
      } catch {
        this.$message.error('Export failed');
      } finally {
        this.exporting = false;
      }
    },

    getCellStyle({ rowIndex }) {
      return {
        backgroundColor: rowIndex % 2 === 0
          ? "rgba(228,228,228,0.2)"
          : "rgba(255,255,255,0.2)",
      };
    },

    formatDate(val) {
      if (!val) return "";
      return String(val).replace("T", " ").substring(0, 10);
    },

    formatDateTime(val) {
      if (!val) return "";
      try {
        const d = new Date(val);
        if (isNaN(d.getTime())) return val;
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
      } catch {
        return val;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.barging-container {
  border-radius: 10px;
}

.searchBar {
  width: 100% !important;
  background-color: #f3f7fa;
  font-size: 11px;
  color: #1d1b20;
  font-family: "Roboto";
  padding: 0 10px;
}

::v-deep .pagination-container {
  background: #f2f7fa;
}

::v-deep .el-table td.el-table__cell {
  vertical-align: top;
}

::v-deep .el-table .cell {
  word-break: break-word;
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
}
</style>
